import { IDropdownOption } from '@fluentui/react';
import { FormEvent, useCallback, useState } from 'react';

import { defaultVersion } from '../../../../../aiem/defaults';
import { useEngine } from '../../../../../aiem/engine/hooks';
import { AIEMOpType, AIEngine, EngineVersion, ValidEngineType } from '../../../../../aiem/engine/types';
import { Engine_Type } from '../../../../../aiem/gen/ai/h2o/engine/v1/engine_pb';

const versionKeyMap = {
  [Engine_Type.DRIVERLESS_AI]: 'daiEngineVersion',
  [Engine_Type.NOTEBOOK]: 'notebookImage',
  [Engine_Type.H2O]: 'h2oEngineVersion',
};

const versionInfoKeyMap = {
  [Engine_Type.DRIVERLESS_AI]: 'daiEngineVersionInfo',
  [Engine_Type.NOTEBOOK]: 'notebookImageInfo',
  [Engine_Type.H2O]: 'h2oEngineVersionInfo',
};

/**
 * Hook for managing engine version/image selection and loading.
 *
 * Responsibilities:
 * - Manages version/image selection and loading for different engine types
 * - Handles version dropdown state and validation
 * - Provides version key mappings for different engine types (DAI, H2O, Notebook)
 * - Validates that selected versions are available in the options list
 *
 * @param engine - The engine being configured
 * @param op - The operation type (create, update, view)
 * @returns Object containing version state, handlers, and validation utilities
 */
export const useEngineVersions = (engine: AIEngine, op: AIEMOpType) => {
  const { listVersions } = useEngine();

  const engineType = engine?.engineType;
  const isNotebookEngine = engineType === Engine_Type.NOTEBOOK;
  const validEngineType = engineType && engineType !== Engine_Type.UNSPECIFIED ? engineType : Engine_Type.DRIVERLESS_AI;
  const versionKey = versionKeyMap[validEngineType];
  const versionInfoKey = versionInfoKeyMap[validEngineType];

  const currentVersion = engine?.[versionKey as keyof AIEngine] as string;
  const initialVersion = op === AIEMOpType.update && currentVersion ? currentVersion : defaultVersion;

  const [selectedVersion, setSelectedVersion] = useState<string>(initialVersion);
  const [versions, setVersions] = useState<IDropdownOption[]>([]);

  const loadVersions = useCallback(async (): Promise<EngineVersion | undefined> => {
    if (!engineType) return;

    let defaultOptionData;
    const versionsData = await listVersions(engineType as ValidEngineType);

    const options =
      versionsData?.map((version: EngineVersion) => {
        const { key, name: text, isDefault } = version;

        if (op === AIEMOpType.create) {
          if ((isDefault || isNotebookEngine) && key) {
            setSelectedVersion(key);
            defaultOptionData = version;
          }
        } else if (op === AIEMOpType.edit) {
          const currentVersion = engine?.[versionKey as keyof AIEngine] as string;
          if (currentVersion === key) {
            setSelectedVersion(key);
            defaultOptionData = version;
          }
        }

        return { key, text, data: version } as IDropdownOption;
      }) || [];

    setVersions(options);
    return defaultOptionData;
  }, [listVersions, engineType, isNotebookEngine]);

  const handleVersionChange = useCallback(
    (_e: FormEvent<HTMLDivElement>, option?: IDropdownOption, onUpdate?: (updates: any) => void) => {
      setSelectedVersion(option?.key as string);
      if (onUpdate) {
        onUpdate({
          [versionKey]: option?.key as string,
          [versionInfoKey]: option?.data,
        });
      }
    },
    [versionKey, versionInfoKey]
  );

  const isVersionInList = versions.some((option) => option.key === selectedVersion);

  return {
    selectedVersion,
    setSelectedVersion,
    versions,
    versionKey,
    versionInfoKey,
    loadVersions,
    handleVersionChange,
    isVersionInList,
    isNotebookEngine,
  };
};
